import { Link, useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import { Button } from '../components/ui/Button';
import DeckList from '../components/DeckList';
import {
  Brain,
  Zap,
  Library,
  ArrowRight,
  Star,
  Clock,
  TrendingUp,
  Sparkles,
  GraduationCap,
} from 'lucide-react';

export const HomePage: React.FC = () => {
  const { decks, getSessionStats } = useAppState();
  const navigate = useNavigate();

  const recentDecks = [...decks]
    .sort((a,b) => {
      const aDate = a.lastStudied ? new Date(a.lastStudied).getTime() : 0;
      const bDate = b.lastStudied ? new Date(b.lastStudied).getTime() : 0;
      return bDate - aDate;
    })
    .slice(0, 3);

  const favoriteDecks = decks.filter((d) => d.isFavorite).slice(0, 3);
  const displayDecks = favoriteDecks.length > 0 ? favoriteDecks : recentDecks;

  const totalCards = decks.reduce((acc, deck) => acc + deck.cards.length, 0);
  const stats = getSessionStats();

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="text-center py-12 md:py-20 space-y-6">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-full text-blue-700 dark:text-blue-300 text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            AI-Powered Flashcards
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
            Learn Smarter with
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              AI Flashcards
            </span>
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Upload any document, image, or text and let AI generate flashcards for you.
            Study smarter, not harder.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap justify-center gap-3 pt-4">
          <Button
            size="lg"
            leftIcon={<Zap className="w-5 h-5" />}
            onClick={() => navigate('/generate')}
          >
            Generate Flashcards
          </Button>
          <Button
            variant="secondary"
            size="lg"
            leftIcon={<GraduationCap className="w-5 h-5" />}
            onClick={() => navigate('/decks')}
          >
            Study Now
          </Button>
          <Button
            variant="outline"
            size="lg"
            leftIcon={<Library className="w-5 h-5" />}
            onClick={() => navigate('/decks')}
          >
            Browse Decks
          </Button>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Decks', value: decks.length, icon: Library, color: 'blue' },
          { label: 'Cards', value: totalCards, icon: Brain, color: 'purple' },
          { label: 'Sessions', value: stats.totalSessions, icon: TrendingUp, color: 'green' },
          { label: 'Reviews', value: stats.totalCards, icon: Clock, color: 'orange' },
        ].map((stat) => {
          const Icon = stat.icon;
          const bgColors: Record<string, string> = {
            blue: 'bg-blue-50 dark:bg-blue-900/30',
            purple: 'bg-purple-50 dark:bg-purple-900/30',
            green: 'bg-green-50 dark:bg-green-900/30',
            orange: 'bg-orange-50 dark:bg-orange-900/30',
          };
          const textColors: Record<string, string> = {
            blue: 'text-blue-600 dark:text-blue-400',
            purple: 'text-purple-600 dark:text-purple-400',
            green: 'text-green-600 dark:text-green-400',
            orange: 'text-orange-600 dark:text-orange-400',
          };
          return (
            <div
              key={stat.label}
              className={`${bgColors[stat.color]} rounded-2xl p-6 text-center`}
            >
              <Icon className={`w-6 h-6 ${textColors[stat.color]} mx-auto mb-2`} />
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {stat.value}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
            </div>
          );
        })}
      </section>

      {/* Recent/Favorite Decks */}
      {displayDecks.length > 0 && (
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              {favoriteDecks.length > 0 ? (
                <>
                  <Star className="w-5 h-5 text-yellow-500" /> Favorite Decks
                </>
              ) : (
                <>
                  <Clock className="w-5 h-5 text-gray-500" /> Recent Decks
                </>
              )}
            </h2>
            <Link
              to="/decks"
              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <DeckList
            decks={displayDecks}
            onSelectDeck={(deck) => navigate(`/decks`)} // In real app, navigate to deck detail
            onStudyDeck={(deck) => navigate(`/study?deck=${deck.id}`)}
          />
        </section>
      )}

      {/* CTA */}
      <section className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-8 md:p-12 text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Ready to learn?
        </h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
          Create your first flashcard deck or generate one from your study materials using AI.
        </p>
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <Button variant="primary" leftIcon={<Zap className="w-5 h-5" />} onClick={() => navigate('/generate')}>
            Generate with AI
          </Button>
          <Button variant="secondary" leftIcon={<Library className="w-5 h-5" />} onClick={() => navigate('/decks')}>
            Create Manually
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
